import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus } from "lucide-react";

export default function CreateUserDialog({ open, onOpenChange, onCreated, allManagers = [] }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: "salesperson", user_type: "salesperson",
    manager_id: "", phone: "",
  });
  const [busy, setBusy] = useState(false);

  const reset = () => setForm({
    name: "", email: "", password: "",
    role: "salesperson", user_type: "salesperson",
    manager_id: "", phone: "",
  });

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Nombre, correo y contraseña son obligatorios");
      return;
    }
    if (form.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
      };
      if (form.role === "salesperson") {
        payload.user_type = form.user_type;
        if (form.user_type === "salesperson" && form.manager_id) {
          payload.manager_id = form.manager_id;
        }
      }
      if (form.phone.trim()) payload.phone = form.phone.trim();
      await api.post("/admin/users", payload);
      toast.success("Usuario creado · ya puede iniciar sesión con esa contraseña");
      onCreated?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear usuario");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-white max-w-lg" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading inline-flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[#1B2E7A]" /> Crear usuario manualmente
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Útil cuando un vendedor no tiene correo corporativo de Google. El usuario podrá iniciar sesión inmediatamente con esta contraseña.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#6B7280]">Nombre completo</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Carlos Mendoza" data-testid="cu-name" />
          </div>
          <div>
            <label className="text-xs text-[#6B7280]">Correo electrónico</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="carlos@canalesfranchise.com" data-testid="cu-email" />
          </div>
          <div>
            <label className="text-xs text-[#6B7280]">Contraseña inicial (mín. 6 caracteres)</label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Comparte esta clave con el vendedor de forma segura" data-testid="cu-password" />
          </div>
          <div>
            <label className="text-xs text-[#6B7280]">Rol</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
              data-testid="cu-role"
            >
              <option value="salesperson">Vendedor</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          {form.role === "salesperson" && (
            <>
              <div>
                <label className="text-xs text-[#6B7280]">Tipo</label>
                <select
                  value={form.user_type}
                  onChange={(e) => setForm({ ...form, user_type: e.target.value })}
                  className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="cu-user-type"
                >
                  <option value="salesperson">Vendedor de piso</option>
                  <option value="manager">Manager (líder de tienda)</option>
                </select>
              </div>
              {form.user_type === "salesperson" && allManagers.length > 0 && (
                <div>
                  <label className="text-xs text-[#6B7280]">Reporta a (opcional)</label>
                  <select
                    value={form.manager_id}
                    onChange={(e) => setForm({ ...form, manager_id: e.target.value })}
                    className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
                    data-testid="cu-manager"
                  >
                    <option value="">— Sin manager asignado —</option>
                    {allManagers.map((m) => (
                      <option key={m.user_id} value={m.user_id}>{m.name} · {m.email}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
          <div>
            <label className="text-xs text-[#6B7280]">Teléfono (opcional, formato E.164)</label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+18175551234" className="font-mono" data-testid="cu-phone" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="cu-submit">
            {busy ? "Creando..." : "Crear usuario"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
